import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Save, Trash2, Tag, Percent, DollarSign } from 'lucide-react';

function GestionPromociones({ paquetes, onRefresh }) {
  const [promociones, setPromociones] = useState([]);
  const [promocionActiva, setPromocionActiva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaConDescuento, setBusquedaConDescuento] = useState('');
  const [paquetesSeleccionados, setPaquetesSeleccionados] = useState([]);
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [editandoPromo, setEditandoPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    nombre: '',
    descripcion: '',
    descuento_valor: '',
    descuento_tipo: 'porcentaje',
    moneda: '$',
    color: '#FF6B6B',
    icono: '🏷️'
  });

  // Cargar promociones
  useEffect(() => {
    fetchPromociones();
  }, []);

  const fetchPromociones = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/promociones`);
      if (response.ok) {
        const data = await response.json();
        setPromociones(data);
        if (data.length > 0 && !promocionActiva) {
          setPromocionActiva(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error cargando promociones:', error);
    } finally {
      setLoading(false);
    }
  };

  // Paquetes con descuento activo (cualquier descuento > 0) + filtro de búsqueda
    const paquetesConDescuento = paquetes.filter(p => 
    parseFloat(p.descuento_principal_monto) > 0
    ).filter(p =>
    p.titulo.toLowerCase().includes(busquedaConDescuento.toLowerCase()) ||
    p.destino_nombre?.toLowerCase().includes(busquedaConDescuento.toLowerCase())
    );

  // Paquetes sin descuento
  const paquetesSinDescuento = paquetes.filter(p => 
    !p.descuento_principal_monto || parseFloat(p.descuento_principal_monto) === 0
  ).filter(p =>
    p.titulo.toLowerCase().includes(busqueda.toLowerCase()) ||
    p.destino_nombre?.toLowerCase().includes(busqueda.toLowerCase())
  );

  const promocionSeleccionada = promociones.find(p => p.id === promocionActiva);

  // Crear/Editar promoción
  const handleSubmitPromo = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = editandoPromo 
        ? `${process.env.REACT_APP_API_URL}/promociones/${editandoPromo.id}`
        : `${process.env.REACT_APP_API_URL}/promociones`;
      
      const response = await fetch(url, {
        method: editandoPromo ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(promoForm)
      });

      if (response.ok) {
        alert(editandoPromo ? '✅ Promoción actualizada' : '✅ Promoción creada');
        setShowCrearModal(false);
        setEditandoPromo(null);
        setPromoForm({
          nombre: '',
          descripcion: '',
          descuento_valor: '',
          descuento_tipo: 'porcentaje',
          moneda: '$',
          color: '#FF6B6B',
          icono: '🏷️'
        });
        fetchPromociones();
      }
    } catch (error) {
      console.error('Error guardando promoción:', error);
      alert('Error al guardar promoción');
    }
  };

  // Eliminar promoción
  const handleEliminarPromo = async (promoId) => {
    if (!window.confirm('¿Eliminar esta promoción?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/promociones/${promoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('✅ Promoción eliminada');
        fetchPromociones();
      }
    } catch (error) {
      console.error('Error eliminando promoción:', error);
    }
  };

  // Aplicar promoción a paquetes seleccionados
  const handleAplicarPromocion = async () => {
    if (paquetesSeleccionados.length === 0) {
      alert('Selecciona al menos un paquete');
      return;
    }

    if (!promocionActiva) {
      alert('Selecciona una promoción');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/promociones/aplicar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          promocionId: promocionActiva,
          paqueteIds: paquetesSeleccionados
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}`);
        setPaquetesSeleccionados([]);
        onRefresh();
      }
    } catch (error) {
      console.error('Error aplicando promoción:', error);
      alert('Error al aplicar promoción');
    }
  };

  // Quitar descuento de un paquete
  const handleQuitarDescuento = async (paqueteId) => {
    if (!window.confirm('¿Quitar el descuento de este paquete?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/promociones/quitar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ paqueteIds: [paqueteId] })
      });

      if (response.ok) {
        alert('✅ Descuento removido');
        onRefresh();
      }
    } catch (error) {
      console.error('Error quitando descuento:', error);
    }
  };

  // Formatear descuento para mostrar
  const formatearDescuento = (paquete) => {
    const valor = parseFloat(paquete.descuento_principal_monto);
    const tipo = paquete.descuento_principal_tipo || 'porcentaje';
    
    if (tipo === 'porcentaje') {
      return `${valor}%`;
    } else {
      const moneda = paquete.moneda || '$';
      return `${moneda} ${valor.toLocaleString('es-AR')}`;
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Cargando promociones...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header con botón crear */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Tag className="text-purple-600" size={20} />
          Gestión de Promociones y Descuentos
        </h3>
        <button
          onClick={() => {
            setEditandoPromo(null);
            setPromoForm({
              nombre: '',
              descripcion: '',
              descuento_valor: '',
              descuento_tipo: 'porcentaje',
              moneda: '$',
              color: '#FF6B6B',
              icono: '🏷️'
            });
            setShowCrearModal(true);
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
        >
          <Plus size={18} />
          Nueva Promoción
        </button>
      </div>

      {/* Tabs de Promociones */}
      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto">
          {promociones.map(promo => (
            <button
              key={promo.id}
              onClick={() => setPromocionActiva(promo.id)}
              className={`px-6 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors flex items-center gap-2 ${
                promocionActiva === promo.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{promo.icono}</span>
              <span>{promo.nombre}</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: promo.color + '20', color: promo.color }}>
                {promo.descuento_tipo === 'porcentaje' 
                  ? `${promo.descuento_valor}%` 
                  : `${promo.moneda} ${parseFloat(promo.descuento_valor).toLocaleString('es-AR')}`
                }
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Contenido */}
      <div className="p-6">
        {promocionSeleccionada && (
          <>
            {/* Info de la promoción activa */}
            <div className="flex items-center justify-between mb-6 p-4 rounded-lg" style={{ backgroundColor: promocionSeleccionada.color + '10' }}>
              <div className="flex items-center gap-4">
                <span className="text-4xl">{promocionSeleccionada.icono}</span>
                <div>
                  <h4 className="text-xl font-bold">{promocionSeleccionada.nombre}</h4>
                  <p className="text-gray-600">{promocionSeleccionada.descripcion}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {promocionSeleccionada.descuento_tipo === 'porcentaje' ? (
                      <span className="flex items-center gap-1 text-lg font-bold" style={{ color: promocionSeleccionada.color }}>
                        <Percent size={18} />
                        {promocionSeleccionada.descuento_valor}% de descuento
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-lg font-bold" style={{ color: promocionSeleccionada.color }}>
                        <DollarSign size={18} />
                        {promocionSeleccionada.moneda} {parseFloat(promocionSeleccionada.descuento_valor).toLocaleString('es-AR')} de descuento
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditandoPromo(promocionSeleccionada);
                    setPromoForm({
                      nombre: promocionSeleccionada.nombre,
                      descripcion: promocionSeleccionada.descripcion || '',
                      descuento_valor: promocionSeleccionada.descuento_valor,
                      descuento_tipo: promocionSeleccionada.descuento_tipo,
                      moneda: promocionSeleccionada.moneda || '$',
                      color: promocionSeleccionada.color,
                      icono: promocionSeleccionada.icono
                    });
                    setShowCrearModal(true);
                  }}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => handleEliminarPromo(promocionSeleccionada.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna Izquierda: Paquetes CON descuento */}
              <div>
                <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                    💰 Paquetes con Descuento Activo
                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs">
                    {paquetesConDescuento.length}
                    </span>
                </h5>
                </div>

                <input
                type="text"
                placeholder="Buscar en paquetes con descuento..."
                value={busquedaConDescuento}
                onChange={(e) => setBusquedaConDescuento(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 text-sm"
                />
                
                {paquetesConDescuento.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-500">No hay paquetes con descuento</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {paquetesConDescuento.map(paquete => {
                    // Formatear fecha
                    const fechaSalida = paquete.fecha_salida 
                        ? new Date(paquete.fecha_salida).toLocaleDateString('es-AR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        })
                        : 'Sin fecha';
                    
                    // Color de disponibilidad
                    const disponibilidad = paquete.disponibilidad || 0;
                    const colorDisponibilidad = disponibilidad === 0 
                        ? 'text-red-600 bg-red-50' 
                        : disponibilidad < 5 
                        ? 'text-orange-600 bg-orange-50' 
                        : 'text-green-600 bg-green-50';
                    
                    // Formatear precio
                    const moneda = paquete.moneda || '$';
                    const precio = paquete.tarifa 
                        ? `${moneda} ${parseFloat(paquete.tarifa).toLocaleString('es-AR')}` 
                        : 'Sin precio';

                    return (
                        <div
                        key={paquete.id}
                        className="p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                        >
                        <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                            {/* Título */}
                            <p className="font-semibold text-gray-900 truncate">{paquete.titulo}</p>
                            
                            {/* Destino y Descuento */}
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-600">{paquete.destino_nombre}</span>
                                <span className="text-sm font-bold text-green-600">
                                -{formatearDescuento(paquete)}
                                </span>
                            </div>
                            
                            {/* Info adicional */}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                {/* Fecha */}
                                <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                📅 {fechaSalida}
                                </span>
                                
                                {/* Disponibilidad */}
                                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-semibold ${colorDisponibilidad}`}>
                                👥 {disponibilidad} cupos
                                </span>
                                
                                {/* Precio */}
                                <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                                💰 {precio}
                                </span>
                                
                                {/* Transporte */}
                                {paquete.transporte && (
                                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    {paquete.transporte === 'Aéreo' || paquete.transporte === 'Aereo' ? '✈️' : '🚌'} {paquete.transporte}
                                </span>
                                )}
                            </div>
                            </div>
                            
                            <button
                            onClick={() => handleQuitarDescuento(paquete.id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded ml-2 flex-shrink-0"
                            title="Quitar descuento"
                            >
                            <X size={18} />
                            </button>
                        </div>
                        </div>
                    );
                    })}
                  </div>
                )}
              </div>

              {/* Columna Derecha: Paquetes SIN descuento */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-semibold text-gray-900">
                    ➕ Aplicar Promoción
                  </h5>
                  {paquetesSeleccionados.length > 0 && (
                    <button
                      onClick={handleAplicarPromocion}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm flex items-center gap-2"
                    >
                      <Tag size={16} />
                      Aplicar ({paquetesSeleccionados.length})
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  placeholder="Buscar paquetes..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3"
                />

                {paquetesSinDescuento.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-500">
                      {busqueda ? 'No se encontraron paquetes' : 'Todos los paquetes tienen descuento'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {paquetesSinDescuento.slice(0, 30).map(paquete => {
                    const fechaSalida = paquete.fecha_salida 
                        ? new Date(paquete.fecha_salida).toLocaleDateString('es-AR', {
                            day: '2-digit',
                            month: '2-digit'
                        })
                        : 'S/F';
                    
                    const disponibilidad = paquete.disponibilidad || 0;
                    
                    return (
                        <label
                        key={paquete.id}
                        className="flex items-center p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                        <input
                            type="checkbox"
                            checked={paquetesSeleccionados.includes(paquete.id)}
                            onChange={(e) => {
                            if (e.target.checked) {
                                setPaquetesSeleccionados(prev => [...prev, paquete.id]);
                            } else {
                                setPaquetesSeleccionados(prev => prev.filter(id => id !== paquete.id));
                            }
                            }}
                            className="mr-3 w-4 h-4 rounded border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{paquete.titulo}</p>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 mt-1">
                            <span>{paquete.destino_nombre}</span>
                            <span className="text-blue-600">📅 {fechaSalida}</span>
                            <span className={disponibilidad < 5 ? 'text-orange-600 font-semibold' : 'text-green-600'}>
                                👥 {disponibilidad}
                            </span>
                            <span className="text-purple-600 font-medium">
                                {paquete.moneda} {parseFloat(paquete.tarifa).toLocaleString('es-AR')}
                            </span>
                            </div>
                        </div>
                        </label>
                    );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal Crear/Editar Promoción */}
      {showCrearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {editandoPromo ? 'Editar Promoción' : 'Nueva Promoción'}
              </h2>
              <button onClick={() => setShowCrearModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitPromo} className="space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={promoForm.nombre}
                  onChange={(e) => setPromoForm(prev => ({ ...prev, nombre: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ej: Descuento Navidad"
                  required
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <input
                  type="text"
                  value={promoForm.descripcion}
                  onChange={(e) => setPromoForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Descripción breve"
                />
              </div>

              {/* Tipo y Valor del descuento */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Descuento</label>
                  <select
                    value={promoForm.descuento_tipo}
                    onChange={(e) => setPromoForm(prev => ({ ...prev, descuento_tipo: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="porcentaje">Porcentaje (%)</option>
                    <option value="monto">Monto Fijo ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor *</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={promoForm.descuento_valor}
                      onChange={(e) => setPromoForm(prev => ({ ...prev, descuento_valor: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder={promoForm.descuento_tipo === 'porcentaje' ? '10' : '20000'}
                      required
                      min="0"
                      step="0.01"
                    />
                    {promoForm.descuento_tipo === 'monto' && (
                      <select
                        value={promoForm.moneda}
                        onChange={(e) => setPromoForm(prev => ({ ...prev, moneda: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="$">$</option>
                        <option value="U$S">U$S</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* Icono y Color */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icono</label>
                  <input
                    type="text"
                    value={promoForm.icono}
                    onChange={(e) => setPromoForm(prev => ({ ...prev, icono: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center text-2xl"
                    placeholder="🏷️"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={promoForm.color}
                      onChange={(e) => setPromoForm(prev => ({ ...prev, color: e.target.value }))}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={promoForm.color}
                      onChange={(e) => setPromoForm(prev => ({ ...prev, color: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCrearModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editandoPromo ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionPromociones;